import React, {useMemo, useRef, useState} from 'react';
import {navigateHook} from "../../../../hooks/use-navigate.js";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {useQuery} from "@tanstack/react-query";
import CapturePlacesService from "../../../../services/web/capture-places-service.js";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import projectUtils from "../../../../utils/project-utils.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import AgGridLinkCellRenderer from "../../../../components/ag-grid/ag-grid-link-cell-renderer.jsx";

const initialFilter = {
    description: "",
    lanes: "",
}

const getCapturePlaceUrls = ({code}) => {
    return {
        urlDev: `/capture-places/form/${code}`,
    };
}

const CapturePlacesList = () => {
    const navigate = navigateHook();

    const [state, setState] = useState(initialFilter)
    const [filter, setFilter] = useState([]);
    const gridRef = useRef(null);
    const theme = useMemo(() => {
        return aggridUtils.generalTheme();
    }, []);

    const { data, isLoading } = useQuery({
        queryFn: () =>  CapturePlacesService.list(JSON.stringify(filter)),
        queryKey: ['route', filter]
    });

    const columnDefs = [
        {headerName: "Nome", field: "description", flex: 1, comparator: aggridUtils.nullLastComparator, sort: 'asc', cellRenderer: AgGridLinkCellRenderer, cellRendererParams: {getUrls: getCapturePlaceUrls}},
        {headerName: "Câmeras ativas", field: "lane_active", flex: 1, comparator: aggridUtils.nullLastComparator, cellRenderer: AgGridLinkCellRenderer, cellRendererParams: {getUrls: getCapturePlaceUrls}},
        {headerName: "Câmeras inativas", field: "lane_inactive", flex: 1, comparator: aggridUtils.nullLastComparator, cellRenderer: AgGridLinkCellRenderer, cellRendererParams: {getUrls: getCapturePlaceUrls}},
    ];

    const apply = () => {
        gridRef.current?.api?.showLoadingOverlay();

        const formattedLanes = state.lanes
            ?.replace(/\s*-\s*/g, '-')
            .replace(/\s*,\s*/g, ',')
            .split(/[,\s]+/)
            .map(part => part.match(/^\d+(-\d+)?$/) ? part : null)
            .filter(Boolean)
            .join(",");

        const regex1 = /^\d+\s*-\s*\d+$/;
        const regex2 = /^[\d,\s]+$/;

        if (formattedLanes && !regex1.test(formattedLanes) && !regex2.test(formattedLanes)) {
            projectUtils.swalErrorMessage(
                "Formato inválido",
                "Use apenas números separados por vírgulas ou um intervalo como 1-4."
            );
            return;
        }

        setFilter([
            {field: 'description', type: 'like', value: state.description},
            {field: 'lane_code', type: 'like', value: formattedLanes ? formattedLanes.replaceAll("'", "") : ""},
        ])
    }

    const clearFilter = () => {
        setState(initialFilter);
        setFilter([]);
    }

    const onChange = ({target}) => {
        let {id, value} = target;

        setState(prev => ({...prev, [id]: value}));
    }

    const newCapture = () => {
        let isBlank = false;
        let urlDev = `/capture-places/form`;
        navigate(urlDev, isBlank);
    }

    useGlobalKey("Enter", () => apply());
    useGlobalKey("Escape", clearFilter);
    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Pontos de coleta</Template.Title>

                <Button target={"primary"} onClick={() => newCapture()}>
                    <FaPlus/> Adicionar ponto
                </Button>
            </Template.Header>

            <Template.Body className="flex flex-col space-y-2">
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-8">
                                <InputForm id="description" value={state.description} onChange={onChange}
                                           label="Nome"/>
                            </div>
                            <div className="flex col-span-4 gap-4">
                                <InputForm classNameWrapper={"w-full"} id="lanes" value={state.lanes} onChange={onChange} mask={/^[\d\s,-]*$/}
                                           label="ID da câmera" title="Apenas números e vírgulas"/>
                                <div className={"flex gap-2"}>
                                    <Button title="Limpar" onClick={clearFilter} target={"primary-outline"}>
                                        <FaEraser />
                                    </Button>
                                    <Button title="Buscar" onClick={apply} >
                                        <FaSearch />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body className="flex">
                        <div className="aggrid-row-pointer h-[calc(100vh-153px)] w-full">
                            <AgGridReact
                                ref={gridRef}
                                rowData={data ?? []}
                                columnDefs={columnDefs}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={theme}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default CapturePlacesList;
